import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import {
  buildListParams,
  parseFilter,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  exportAgencyOverlappingPaymentsToCsv,
  getAgencyOverlappingPayments,
} from "common/services/payments.service";
import TableContainer from "../../../Components/Common/TableContainer";

import ExportLoadingModal from "Components/Common/ExportLoadingModal";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";

import BreadCrumb from "Components/Common/BreadCrumb";
import { useProfile } from "Components/Hooks/UserHooks";
import { normalize } from "common/lib/normalize";
import { isAllowed } from "common/lib/permissionUtils";
import * as moment from "moment";
import { toast } from "react-toastify";
import Filters from "./Filters";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  positionType: string;
  agency: string;
  dateRange: DateRange;
  region: string;
  mda: string;
  paymentCategory: string;
  thematicArea: string;
  sector: string;
  resolved?: string;
}

const defaultSort = [{ id: "createdAt", desc: true }];

const defaultFilterValue: FilterValue = {
  positionType: "",
  agency: "",
  dateRange: {
    fromDate: null,
    toDate: null,
  },
  region: "",
  mda: "",
  paymentCategory: "",
  thematicArea: "",
  sector: "",
  resolved: "All",
};

const AgencyOverlappingPayments = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: parsePage(searchParams),
    pageSize: parsePageSize(searchParams),
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => parseSort(searchParams.get("sort"), defaultSort));

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));
  
  const [filterValue, setFilterValue] = useState(() => parseFilter(searchParams, defaultFilterValue));

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});
  const [exporting, setExporting] = useState(false);
  const getCsvExport = useCallback(async () => {
    setExporting(true);
    await exportAgencyOverlappingPaymentsToCsv({
      filters: apiParams?.filters,
      sort: apiParams?.sort,
    })
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Agency_Overlapping_Payments_Export.csv");
        link.click();
        link.remove();
      })
      .catch(() => {
        toast.error("An error occurred while exporting overlapping payments");
      });
    setExporting(false);
  }, [apiParams]);
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultFilterValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      // andConditions.push({
      //   $or: [
      //     {
      //       payment: {
      //         agency: {
      //           id: {
      //             $eq: filterValue?.agency.value,
      //           },
      //         },
      //       },
      //     },
      //     {
      //       overlappingPayment: {
      //         agency: {
      //           id: {
      //             $eq: filterValue?.agency.value,
      //           },
      //         },
      //       },
      //     },
      //   ],
      // });

      if (filterValue?.region) {
        andConditions.push({
          $or: [
            {
              payment: {
                mda: {
                  region: {
                    id: {
                      $eq: filterValue?.region.value,
                    },
                  },
                },
              },
            },
            {
              overlappingPayment: {
                mda: {
                  region: {
                    id: {
                      $eq: filterValue?.region.value,
                    },
                  },
                },
              },
            },
          ],
        });
      }
      if (filterValue?.mda) {
        andConditions.push({
          $or: [
            {
              payment: {
                mda: {
                  id: {
                    $eq: filterValue?.mda.value,
                  },
                },
              },
            },
            {
              overlappingPayment: {
                mda: {
                  id: {
                    $eq: filterValue?.mda.value,
                  },
                },
              },
            },
          ],
        });
      }
      if (filterValue?.paymentCategory) {
        andConditions.push({
          $or: [
            {
              payment: {
                paymentCategory: {
                  id: {
                    $eq: filterValue?.paymentCategory.value,
                  },
                },
              },
            },
            {
              overlappingPayment: {
                paymentCategory: {
                  id: {
                    $eq: filterValue?.paymentCategory.value,
                  },
                },
              },
            },
          ],
        });
      }

      if (filterValue?.positionType) {
        andConditions.push({
          $or: [
            {
              payment: {
                positionType: {
                  id: {
                    $eq: filterValue.positionType.value,
                  },
                },
              },
            },
            {
              overlappingPayment: {
                positionType: {
                  id: {
                    $eq: filterValue.positionType.value,
                  },
                },
              },
            },
          ],
        });
      }

      if (filterValue?.resolved === "Resolved") {
        andConditions.push({
          resolved: true,
        });
      } else if (filterValue?.resolved === "Unresolved") {
        andConditions.push({
          resolved: false,
        });
      } else if (filterValue?.resolved === "All") {
        andConditions.push({
          resolved: {
            $in: [true, false, null],
          },
        });
      }

      andConditions.push({
        $or: [
          {
            payment: {
              individual: {
                fullName: {
                  $containsi: searchValue,
                },
              },
            },
          },
          {
            overlappingPayment: {
              individual: {
                fullName: {
                  $containsi: searchValue,
                },
              },
            },
          },
          {
            payment: {
              positionTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            overlappingPayment: {
              positionTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            payment: {
              projectOrFund: {
                $containsi: searchValue,
              },
            },
          },
          {
            overlappingPayment: {
              projectOrFund: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);

        const APIParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "payment",
            "payment.agency",
            "payment.mda",
            "payment.mda.region",
            "payment.individual",
            "overlappingPayment",
            "overlappingPayment.agency",
            "overlappingPayment.mda",
            "overlappingPayment.mda.region",
            "overlappingPayment.individual",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(APIParams);
        const response: any = await getAgencyOverlappingPayments(APIParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams]
  );

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    status: isAllowed(userProfile?.permissions, [
      {
        resource: "overlapping-payment",
        action: "update",
      },
    ]),
    updatedAt: isAllowed(userProfile?.permissions, [
      {
        resource: "overlapping-payment",
        action: "update",
      },
    ]),
    "payment.createdAt": isAllowed(userProfile?.permissions, [
      {
        resource: "overlapping-payment",
        action: "create",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: "Ministry/Department/Agency (MDA)",
        accessorKey: "payment.mda.name",
        id: "payment.mda.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  whiteSpace: "pre-wrap",
                  width: "400px",
                }}
              >
                {/* Rendered as plain text, not links: the whole row navigates
                    to the pair's detail page, so cell content must not
                    intercept the click. */}
                <div className="flex-grow-1">
                  {/* Dots mark which line is the first payment and which is the
                      overlapping one, matching the admin table. */}
                  <h5 className="fs-14 mb-2">
                    <i
                      className="ri-checkbox-blank-circle-fill fs-10 me-2 align-middle text-body"
                      title="First payment"
                    ></i>
                    <span className="text-body">
                      {`${rowData?.payment?.mda?.name} - ${rowData?.payment?.mda?.region?.name}`}
                      {rowData?.payment?.mda?.acronym
                        ? ` (${rowData?.payment?.mda?.acronym})`
                        : ""}
                    </span>
                  </h5>
                  <h5 className="fs-14 mb-1 text-muted">
                    <i
                      className="ri-checkbox-blank-circle-fill fs-10 me-2 align-middle"
                      title="Overlapping payment"
                    ></i>
                    <span className="text-muted">
                      {`${rowData?.overlappingPayment?.mda?.name} - ${rowData?.overlappingPayment?.mda?.region?.name}`}
                      {rowData?.overlappingPayment?.mda?.acronym
                        ? ` (${rowData?.overlappingPayment?.mda?.acronym})`
                        : ""}
                    </span>
                  </h5>
                </div>
              </div>
            </>
          );
        },
      },

      {
        header: "Agency",
        accessorKey: "payment.agency.name",
        enableColumnFilter: false,
        id: "payment.agency.name",
        cell: (cell: any) => (
          <div
            className="d-flex align-items-center"
            style={{
              whiteSpace: "pre-wrap",
              width: "100px",
            }}
          >
            <div className="flex-grow-1">
              <div className="fs-14 mb-1">
                {cell.row.original?.payment?.agency?.name || "Not Provided"}
              </div>
              <div className="fs-14 mb-1 text-muted">
                {cell.row.original?.overlappingPayment?.agency?.name ||
                  "Not Provided"}
              </div>
            </div>
          </div>
        ),
      },

      {
        header: "Individual Name",
        accessorKey: "payment.individual.fullName",
        enableColumnFilter: false,
        id: "payment.individual.fullName",
        cell: (cell: any) => (
          <div
            className="d-flex align-items-center"
            style={{
              whiteSpace: "pre-wrap",
              width: "200px",
            }}
          >
            <div className="flex-grow-1">
              <div className="fs-14 mb-1 text-body">
                {cell.row.original?.payment?.individual?.fullName ||
                  "Not Provided"}
              </div>
              <div className="fs-14 mb-1 text-muted">
                {cell.row.original?.overlappingPayment?.individual?.fullName ||
                  "Not Provided"}
              </div>
            </div>
          </div>
        ),
      },

      {
        header: "Position Title",
        accessorKey: "payment.positionTitle",
        enableColumnFilter: false,
        id: "payment.positionTitle",
        cell: (cell: any) => (
          <div
            className="d-flex align-items-center"
            style={{
              whiteSpace: "pre-wrap",
              width: "200px",
            }}
          >
            <div className="flex-grow-1">
              <div className="fs-14 mb-1">
                {cell.row.original?.payment?.positionTitle || "Not Provided"}
              </div>
              <div className="fs-14 mb-1 text-muted">
                {cell.row.original?.overlappingPayment?.positionTitle ||
                  "Not Provided"}
              </div>
            </div>
          </div>
        ),
      },

      {
        header: "Start Date",
        accessorKey: "payment.startDate",
        id: "payment.startDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          if (rowData?.payment?.startDate === null) return null;
          const paymentDate = moment(
            new Date(rowData?.payment?.startDate)
          ).format("DD MMM Y");
          const overlappingPaymentDate = moment(
            new Date(rowData?.overlappingPayment?.startDate)
          ).format("DD MMM Y");
          return (
            <div
              className="d-flex align-items-center"
              style={{
                whiteSpace: "pre-wrap",
                width: "100px",
              }}
            >
              <div className="flex-grow-1">
                <div className="fs-14 mb-1">
                  {(rowData?.payment?.startDate && paymentDate) ||
                    "Not Provided"}
                </div>
                <div className="fs-14 mb-1 text-muted">
                  {(rowData?.overlappingPayment?.startDate &&
                    overlappingPaymentDate) ||
                    "Not Provided"}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: "End Date",
        accessorKey: "payment.endDate",
        id: "payment.endDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          if (rowData?.payment?.endDate === null) return null;
          const paymentDate = moment(
            new Date(rowData?.payment?.endDate)
          ).format("DD MMM Y");
          const overlappingPaymentDate = moment(
            new Date(rowData?.overlappingPayment?.endDate)
          ).format("DD MMM Y");
          return (
            <div
              className="d-flex align-items-center"
              style={{
                whiteSpace: "pre-wrap",
                width: "100px",
              }}
            >
              <div className="flex-grow-1">
                <div className="fs-14 mb-1">
                  {(rowData?.payment?.endDate && paymentDate) || "Not Provided"}
                </div>
                <div className="fs-14 mb-1 text-muted">
                  {(rowData?.overlappingPayment?.endDate &&
                    overlappingPaymentDate) ||
                    "Not Provided"}
                </div>
              </div>
            </div>
          );
        },
      },

      {
        header: "Amount / Month",
        accessorKey: "payment.amountPerMonth",
        enableColumnFilter: false,
        id: "payment.amountPerMonth",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const paymentAmount = rowData?.payment?.amountPerMonth;
          const overlappingPaymentAmount =
            rowData?.overlappingPayment?.amountPerMonth;
          return (
            <div
              className="d-flex align-items-center"
              style={{
                whiteSpace: "pre-wrap",
                width: "100px",
              }}
            >
              <div className="flex-grow-1">
                <div className="fs-14 mb-1">
                  {(paymentAmount && `$ ${paymentAmount.toLocaleString()}`) ||
                    "Not Provided"}
                </div>
                <div className="fs-14 mb-1 text-muted">
                  {(overlappingPaymentAmount &&
                    `$ ${overlappingPaymentAmount.toLocaleString()}`) ||
                    "Not Provided"}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const resolved = cell.row.original.resolved;
          return (
            <React.Fragment>
              {resolved === null ? (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  Not Provided
                </span>
              ) : resolved ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  Resolved
                </span>
              ) : resolved === false ? (
                <span className="badge bg-danger-subtle text-danger fs-11">
                  Unresolved
                </span>
              ) : null}
            </React.Fragment>
          );
        },
      },

      {
        header: "Created At",
        accessorKey: "payment.createdAt",
        id: "payment.createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const paymentDate = moment(
            new Date(rowData?.payment?.createdAt)
          ).format("DD MMM Y");
          const overlappingPaymentDate = moment(
            new Date(rowData?.overlappingPayment?.createdAt)
          ).format("DD MMM Y");

          return (
            <div
              className="d-flex align-items-center"
              style={{
                whiteSpace: "pre-wrap",
                width: "100px",
              }}
            >
              <div className="flex-grow-1">
                <div className="fs-14 mb-1">
                  {paymentDate || "Not Provided"}
                </div>
                <div className="fs-14 mb-1 text-muted">
                  {overlappingPaymentDate || "Not Provided"}
                </div>
              </div>
            </div>
          );
        },
      },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="overlapping-payment" action="findOne">
                  <DropdownItem
                    href={`/agency/overlapping-payments/${rowData?.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/agency/overlapping-payments/${rowData?.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View Payment
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={`${userProfile?.agency?.name} Overlapping Payments`}
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <Filters
            setSearchValue={setSearchValue}
            searchValue={searchValue}
            setFilterValue={setFilterValue}
            filterValue={filterValue}
            setPage={setPage}
            setSorted={setSorted}
            defaultFilterValue={defaultFilterValue}
          />

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <h5 className="card-title mb-1">
                    {userProfile?.agency?.name} Overlapping Payments
                  </h5>
                  <p className="mb-0 text-muted">
                    A list of payments and contracts that overlap with{" "}
                    {userProfile?.agency?.name}.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2 align-items-center">
                    <Permissions
                      checks={[
                        {
                          resource: "overlapping-payment",
                          action: "exportToCsv",
                          controller: "agency",
                        },
                      ]}
                    >
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="button"
                          className="btn btn-light"
                          id="agencyOverlappingPaymentsActionsDropdown"
                        >
                          <i className="ri ri-more-fill"></i>
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-end">
                          <DropdownItem
                            onClick={() => {
                              getCsvExport();
                            }}
                          >
                            <i className="ri ri-download-2-line me-1"></i>
                            Export
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </Permissions>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              {/* Boxed so it reads as a key for the table rather than stray
                  text floating above the header row. */}
              {/* The table below carries `.table-card`, whose -16px margin-top
                  swallows 16px of whatever gap is set here. Adding that back on
                  top of the 16px we want leaves the space above and below the
                  legend equal to the card body's own 16px padding. */}
              <div
                className="d-flex justify-content-start"
                style={{ marginBottom: "32px" }}
              >
                <div className="d-inline-flex align-items-center flex-wrap gap-2 border rounded bg-light px-3 py-2 fs-12">
                  <span className="text-muted">Each row compares</span>
                  <span className="d-inline-flex align-items-center fw-medium">
                    <i className="ri-checkbox-blank-circle-fill fs-10 me-1"></i>
                    First payment
                  </span>
                  <span className="text-muted">vs</span>
                  <span className="d-inline-flex align-items-center text-muted">
                    <i className="ri-checkbox-blank-circle-fill fs-10 me-1"></i>
                    Overlapping payment
                  </span>
                </div>
              </div>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                onRowClick={(rowData: any) =>
                  navigate(`/agency/overlapping-payments/${rowData?.id}`)
                }
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap "
              />

              <ExportLoadingModal show={exporting} onCloseClick={() => {}} />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AgencyOverlappingPayments;
